import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Typography, Button } from 'antd';
import { Modal } from 'components/Base/AntdModal';
import styled from 'styled-components';
import {
  ENotificationType,
  useShowToastNotification,
  useTranslation,
} from '@packages/utils';
import {
  useGetWorkshopPartKitNotesQuery,
  useUpdateWorkshopPartKitNotesMutation,
  useDeleteWorkshopPartKitNoteMutation,
  TWorkshopPartKitNoteUI,
  TWorkshopPartKitNotesResponse,
} from 'redux/apiSlice/workshops.slice';
import {
  useGetWorkshopPartKitNotesQuery as useGetOrgKitNotesQuery,
  useUpdateWorkshopPartKitNotesMutation as useUpdateOrgKitNotesMutation,
  useDeleteWorkshopPartKitNoteMutation as useDeleteOrgKitNoteMutation,
  TOrganizationKitNoteUI,
  TOrganizationKitNotesResponse,
} from 'redux/apiSlice/organizationKitInventory.slice';
import { PlusOutlined } from '@ant-design/icons';
import { DeleteTrashOutline } from 'assets/icon/icon';
import EditableReachTextEditor from 'components/Base/EditableFields/EditableReachTextEditor';

const { Text } = Typography;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    padding: 0;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: none;
    border: none;
  }

  .ant-modal-header {
    background-color: white;
    padding: 24px;
    margin: 0;
    border-bottom: none;
  }

  .ant-modal-title {
    color: #1b1a1c;
    font-family: Montserrat;
    font-size: 24px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
  }

  .ant-modal-body {
    padding: 0 24px 24px;
  }

  .ant-modal-footer {
    display: flex;
    justify-content: flex-end;
    padding: 16px 24px;
    margin-top: 24px;
    border-top: none;
  }

  .ant-modal-close {
    top: 20px;
    right: 20px;
  }

  .ant-modal-close-x {
    font-size: 42px;
    font-weight: 300;
    width: 24px;
    height: 24px;
    line-height: 24px;
  }
`;

const NoteCard = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  background-color: #fff;
  position: relative;
`;

const NoteDate = styled(Text)`
  display: block;
  color: #888;
  font-size: 16px;
  margin-bottom: 8px;
`;

const DeleteButton = styled(Button)`
  position: absolute;
  top: 16px;
  right: 16px;
  border: none;
  background: transparent;
  color: #888;
  padding: 0;
  &:hover {
    color: #ff4d4f;
    background: transparent;
  }
`;

const AddNoteButton = styled(Button)`
  color: var(--Selected-green, #0077a0);
  font-family: Lato;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  background: transparent;
  border: none;
  padding-left: 0;
  display: flex;
  align-items: center;
  box-shadow: none !important;

  &.ant-btn-default {
    box-shadow: none;
    border: none;
  }

  &:hover {
    color: #1890ff;
    background: transparent;
  }

  .anticon {
    font-size: 18px;
  }
`;

const EditorContainer = styled.div`
  .rich-text-editor {
    min-height: 100px;
  }

  .ant-typography {
    padding: 0;
    margin: 0;
  }
`;

const GotItButton = styled(Button)`
  display: flex;
  padding: 0px 8px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  color: #1b1a1c;
  font-family: Lato;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
`;

const NotesScrollContainer = styled.div`
  max-height: 500px;
  overflow-y: auto;
  padding-right: 8px;
  margin-bottom: 16px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }

  @media (max-height: 700px) {
    max-height: 200px;
  }

  @media (min-height: 900px) {
    max-height: 400px;
  }
`;

interface TWorkshopPartKitNoteUIWithDate extends TWorkshopPartKitNoteUI {
  date?: string;
}

export interface NotesApiHooks {
  useGetNotes: (
    params: { workshopId: string; partId: string },
    options?: any,
  ) => {
    data?: any[];
    refetch: () => void;
    isLoading: boolean;
  };
  useUpdateNotes: any;
  useDeleteNote: any;
}

export interface NotesModalProps {
  visible: boolean;
  onClose: () => void;
  workshopId: string;
  partId: string;
  apiHooks?: NotesApiHooks;
  translationKeyPrefix?: string;
  useOrganizationEndpoints?: boolean;
}

const NotesModal: React.FC<NotesModalProps> = ({
  visible,
  onClose,
  workshopId,
  partId,
  apiHooks,
  translationKeyPrefix,
  useOrganizationEndpoints,
}) => {
  const { t } = useTranslation({
    keyPrefix:
      translationKeyPrefix ||
      'descriptions.admin-portal.workshops.details.kits',
  });
  const [localNotes, setLocalNotes] = useState<
    TWorkshopPartKitNoteUIWithDate[]
  >([]);
  const [newNote, setNewNote] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const { showToastNotification } = useShowToastNotification();
  const [latestNoteEmpty, setLatestNoteEmpty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const modalBodyRef = useRef<HTMLDivElement>(null);
  const newNoteEditorRef = useRef<any>(null);
  const focusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handlerRef = useRef<((e: MouseEvent) => void) | undefined>(undefined);

  useEffect(
    () => () => {
      if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
    },
    [],
  );

  const defaultGetNotes = useOrganizationEndpoints
    ? useGetOrgKitNotesQuery
    : useGetWorkshopPartKitNotesQuery;
  const defaultUpdateNotes = useOrganizationEndpoints
    ? useUpdateOrgKitNotesMutation
    : useUpdateWorkshopPartKitNotesMutation;
  const defaultDeleteNote = useOrganizationEndpoints
    ? useDeleteOrgKitNoteMutation
    : useDeleteWorkshopPartKitNoteMutation;

  const {
    data: notesData,
    refetch,
    isLoading: isLoadingNotes,
  } = (apiHooks?.useGetNotes || defaultGetNotes)(
    { workshopId, partId },
    {
      skip: !visible || !workshopId || !partId,
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: false,
      refetchOnFocus: false,
    },
  );

  const [updateNotes] = (apiHooks?.useUpdateNotes || defaultUpdateNotes)();
  const [deleteNote] = (apiHooks?.useDeleteNote || defaultDeleteNote)();

  useEffect(() => {
    if (visible && workshopId && partId && !notesData) {
      refetch();
    }
  }, [visible, workshopId, partId, notesData, refetch]);
  useEffect(() => {
    if (visible && workshopId && partId) {
      refetch();
    }
  }, [partId, visible, workshopId, refetch]);

  useEffect(() => {
    if (notesData && notesData.length > 0 && visible) {
      setLocalNotes(
        notesData.map(
          (
            note: TWorkshopPartKitNotesResponse | TOrganizationKitNotesResponse,
          ) => ({
            noteId: String(note.noteId),
            note: note.note,
            date: (note as any).date || (note as any).createdAt,
          }),
        ),
      );
    } else if (notesData && visible) {
      setLocalNotes([]);
    }
  }, [notesData, visible, useOrganizationEndpoints]);

  useEffect(() => {
    if (localNotes.length > 0) {
      const latestNote = localNotes[localNotes.length - 1];
      const noteContent = latestNote.note.replace(/<\/?[^>]+(>|$)/g, '').trim();
      setLatestNoteEmpty(noteContent === '');
    } else {
      setLatestNoteEmpty(false);
    }
  }, [localNotes]);

  const focusEditor = (ref: React.RefObject<any>) => {
    if (!ref.current) return;

    if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
    focusTimerRef.current = setTimeout(() => {
      if (ref.current?.focus) {
        ref.current.focus();
      } else if (ref.current?.querySelector) {
        const editableDiv = ref.current.querySelector('[contenteditable=true]');
        if (editableDiv) {
          editableDiv.click();

          editableDiv.focus();

          if (window.getSelection && document.createRange) {
            const range = document.createRange();
            range.selectNodeContents(editableDiv);
            range.collapse(false);
            const selection = window.getSelection();
            if (selection) {
              selection.removeAllRanges();
              selection.addRange(range);
            }
          }
        } else {
          const editorArea = ref.current.querySelector(
            '.rich-text-editor, .public-DraftEditor-content, [role="textbox"]',
          );
          if (editorArea) {
            editorArea.click();
            editorArea.focus();
          }
        }
      }
    }, 100);
  };

  useEffect(() => {
    if (showAddForm && newNoteEditorRef.current) {
      focusEditor(newNoteEditorRef);
    }
  }, [showAddForm]);

  const handleAddNote = () => {
    setNewNote('');
    setShowAddForm(true);
  };

  const handleNoteChange = (value: string) => {
    setNewNote(value);
  };

  const hasRealContent = useCallback((note: string) => {
    const cleanedText = note
      .replace(/<\/?[^>]+(>|$)/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();

    return cleanedText.length > 0;
  }, []);

  const handleSaveToServer = useCallback(
    async (notes: TWorkshopPartKitNoteUI[]) => {
      if (isSaving) return;

      let saveSucceeded = false;

      try {
        setIsSaving(true);

        const notesToSend = useOrganizationEndpoints
          ? notes.map(
              (note): TOrganizationKitNoteUI => ({
                noteId: note.noteId ? parseInt(note.noteId, 10) : null,
                text: note.note,
              }),
            )
          : notes;

        await updateNotes({
          workshopId,
          partId,
          notes: notesToSend,
        }).unwrap();

        saveSucceeded = true;
        showToastNotification({
          message: t('notes.SUCCESS_MESSAGE', 'Notes saved successfully'),
          type: ENotificationType.SUCCESS,
        });
      } catch (error) {
        if (visible && !saveSucceeded) {
          showToastNotification({
            message: t('notes.ERROR_MESSAGE', 'Failed to save notes'),
            type: ENotificationType.ERROR,
          });
        }
      } finally {
        setIsSaving(false);
      }
    },
    [
      isSaving,
      updateNotes,
      workshopId,
      partId,
      showToastNotification,
      t,
      visible,
      useOrganizationEndpoints,
    ],
  );

  const handleAddNoteToList = useCallback(() => {
    if (!hasRealContent(newNote)) {
      setShowAddForm(false);
      return;
    }

    const cleanedNote = newNote;

    const newUnsavedNote = {
      noteId: null,
      note: cleanedNote,
    };

    const updatedNotes = [...localNotes, newUnsavedNote];
    handleSaveToServer(updatedNotes);

    setNewNote('');
    setShowAddForm(false);
  }, [newNote, localNotes, handleSaveToServer, hasRealContent]);

  useEffect(() => {
    let saveSuccessful = false;

    handlerRef.current = (e: MouseEvent) => {
      if (!newNoteEditorRef.current) return;

      if (saveSuccessful) return;

      if (newNoteEditorRef.current.contains(e.target as Node)) {
        return;
      }

      if (hasRealContent(newNote) && !isSaving) {
        e.preventDefault();
        saveSuccessful = true;
        handleAddNoteToList();
      } else {
        setShowAddForm(false);
      }
    };
  }, [showAddForm, newNote, handleAddNoteToList, isSaving]);

  useEffect(() => {
    if (!showAddForm) return undefined;

    const dispatcher = (e: MouseEvent) => handlerRef.current?.(e);

    document.addEventListener('click', dispatcher, true);

    return () => {
      document.removeEventListener('click', dispatcher, true);
    };
  }, [showAddForm]);

  const handleDeleteNote = useCallback(
    async (noteId: string | undefined) => {
      if (!noteId) return;

      try {
        await deleteNote({
          workshopId,
          partId,
          noteId,
        }).unwrap();

        showToastNotification({
          message: t('notes.DELETE_SUCCESS', 'Note deleted successfully'),
          type: ENotificationType.SUCCESS,
        });

        const updatedNotes = localNotes.filter(note => note.noteId !== noteId);
        setLocalNotes(updatedNotes);
      } catch (error) {
        showToastNotification({
          message: t('notes.DELETE_ERROR', 'Failed to delete note'),
          type: ENotificationType.ERROR,
        });
      }
    },
    [localNotes, deleteNote, workshopId, partId, showToastNotification, t],
  );

  const handleCancel = () => {
    onClose();
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!visible) {
      setIsSaving(false);
      setShowAddForm(false);
      setNewNote('');
      setLatestNoteEmpty(false);
    }
  }, [visible]);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);

      if (Number.isNaN(date.getTime())) {
        return 'Invalid Date';
      }

      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      };
      return date.toLocaleDateString('en-US', options);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Date error';
    }
  };

  const handleNoteContentChange = useCallback(
    (noteId: string, value: string) => {
      const cleanedContent = value;

      if (!hasRealContent(cleanedContent) || isSaving) {
        return;
      }

      const updatedNotes = localNotes.map(note =>
        note.noteId === noteId ? { ...note, note: cleanedContent } : note,
      );

      handleSaveToServer(updatedNotes);
    },
    [localNotes, isSaving, handleSaveToServer, hasRealContent],
  );

  const renderNoteCard = useCallback(
    (note: TWorkshopPartKitNoteUIWithDate) => {
      return (
        <NoteCard key={note.noteId}>
          <DeleteButton
            icon={<DeleteTrashOutline size={16} fill="#888" />}
            onClick={() => handleDeleteNote(note.noteId || undefined)}
          />
          <NoteDate>
            {note.date
              ? formatDate(note.date)
              : formatDate(new Date().toISOString())}
          </NoteDate>
          <EditorContainer>
            <EditableReachTextEditor
              title={note.note}
              fitContent={false}
              onEdit={value =>
                handleNoteContentChange(note.noteId || '', value)
              }
              emptyText="Add content"
              placeholder="Edit note..."
              readonly={false}
              required
            />
          </EditorContainer>
        </NoteCard>
      );
    },
    [handleDeleteNote, handleNoteContentChange],
  );

  return (
    <StyledModal
      title="Notes"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      centered
      closeIcon={<span>×</span>}
    >
      <div ref={modalBodyRef}>
        {isLoadingNotes ? (
          <div>Loading notes...</div>
        ) : (
          <>
            <NotesScrollContainer>
              {localNotes.map(note => renderNoteCard(note))}
            </NotesScrollContainer>

            <>
              {showAddForm ? (
                <div
                  style={{
                    marginTop: 16,
                    marginBottom: 16,
                    border: '1px solid #f0f0f0',
                    borderRadius: '8px',
                    padding: '16px',
                    backgroundColor: '#fff',
                  }}
                  ref={newNoteEditorRef}
                >
                  <EditorContainer>
                    <EditableReachTextEditor
                      title=""
                      fitContent={false}
                      onEdit={handleNoteChange}
                      emptyText="Add content"
                      placeholder="Add a new note..."
                      readonly={false}
                      required
                    />
                  </EditorContainer>
                </div>
              ) : (
                <AddNoteButton
                  onClick={handleAddNote}
                  disabled={latestNoteEmpty || showAddForm}
                >
                  <PlusOutlined /> Add Note
                </AddNoteButton>
              )}
            </>

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                marginTop: '24px',
              }}
            >
              <GotItButton onClick={handleCancel}>Got It</GotItButton>
            </div>
          </>
        )}
      </div>
    </StyledModal>
  );
};

export default NotesModal;
